import { CountrySelect } from '@/components/ui/country-select';
import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { WEBPAGE_TYPE_OPTIONS } from '@/pages/RegulatoryWatch/labels';
import {
  useAdminInstitutionsService,
  useAdminSourcesService,
  useAdminUpdateSourceService,
  useAdminUploadSourceIconService,
} from '@/services/backoffice';
import { getSourceIconUrl } from '@/utils/source-icon';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { useEffect, useRef, useState, type FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export const AdminEditSourcePage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sourceId } = useParams<{ sourceId: string }>();
  const { sources, isLoading: sourcesLoading } = useAdminSourcesService();

  const source = sources.find(s => s.id === sourceId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [geography, setGeography] = useState('');
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [webpageType, setWebpageType] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [hasExistingIcon, setHasExistingIcon] = useState(false);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [initialized, setInitialized] = useState(false);

  const { updateSource, isPending } = useAdminUpdateSourceService();
  const { uploadIcon } = useAdminUploadSourceIconService();
  const { institutions } = useAdminInstitutionsService();

  useEffect(() => {
    if (source && !initialized) {
      setTitle(source.title);
      setDescription(source.description ?? '');
      setGeography(source.geography);
      setInstitutionId(source.institutionId ?? null);
      setUrl(source.url ?? '');
      setWebpageType(source.webpageType ?? '');
      setHasExistingIcon(!!source.icon);
      setInitialized(true);
    }
  }, [source, initialized]);

  useEffect(() => {
    setInitialized(false);
  }, [sourceId]);

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const currentIconUrl =
    iconPreview ??
    (hasExistingIcon && source
      ? getSourceIconUrl(source.id, source.icon)
      : null) ??
    null;

  const handleSubmit = () => {
    if (!title || !source) return;
    updateSource(
      {
        id: source.id,
        body: {
          title,
          description: description || null,
          geography,
          institutionId,
          url: url || null,
          webpageType: webpageType || null,
          icon: hasExistingIcon || iconFile ? undefined : null,
        },
      },
      {
        onSuccess: () => {
          if (iconFile) {
            uploadIcon({ sourceId: source.id, file: iconFile });
          }
          navigateBack();
        },
      }
    );
  };

  const navigateBack = () => {
    navigate(
      `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.sources}${location.search}`
    );
  };

  if (sourcesLoading) {
    return (
      <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </FlexCol>
    );
  }

  if (!source) {
    return (
      <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
        <Button variant="ghost" onClick={navigateBack} className="w-fit gap-2">
          <ArrowLeft size={16} />
          Back to Sources
        </Button>
        <p className="text-sm text-muted-foreground">Source not found.</p>
      </FlexCol>
    );
  }

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={navigateBack} className="gap-2">
          <ArrowLeft size={16} />
          Back to Sources
        </Button>
        <h2 className="text-xl font-semibold">Edit Source</h2>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4 max-w-4xl">
        <div className="space-y-2">
          <Label>ID</Label>
          <div className="text-sm text-muted-foreground px-3 py-2 border rounded-md bg-muted/50">
            {source.id}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Geography</Label>
            <CountrySelect
              value={geography || null}
              onChange={val => setGeography(val ?? '')}
              placeholder="Select geography"
            />
          </div>

          <div className="space-y-2">
            <Label>Institution (optional)</Label>
            <Select
              value={institutionId ?? '__none__'}
              onValueChange={val =>
                setInstitutionId(val === '__none__' ? null : val)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="No institution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {institutions.map(inst => (
                  <SelectItem key={inst.id} value={inst.id}>
                    {inst.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>URL (optional)</Label>
            <Input
              placeholder="e.g. https://example.com/news"
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Webpage type (optional)</Label>
            <Select
              value={webpageType || '__none__'}
              onValueChange={v => setWebpageType(v === '__none__' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select webpage type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {WEBPAGE_TYPE_OPTIONS.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Icon (optional)</Label>
          <div className="flex items-center gap-3">
            {currentIconUrl ? (
              <div className="relative">
                <img
                  src={currentIconUrl}
                  alt="Icon preview"
                  className="w-10 h-10 object-contain rounded border"
                />
                <button
                  type="button"
                  onClick={() => {
                    setIconFile(null);
                    setIconPreview(null);
                    setHasExistingIcon(false);
                    if (iconInputRef.current) iconInputRef.current.value = '';
                  }}
                  className="absolute -top-1 -right-1 rounded-full bg-destructive text-white w-4 h-4 flex items-center justify-center"
                >
                  <X size={10} />
                </button>
              </div>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => iconInputRef.current?.click()}
            >
              <Upload size={14} className="mr-1" />
              {currentIconUrl ? 'Change' : 'Upload icon'}
            </Button>
            <input
              ref={iconInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
              onChange={handleIconChange}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex-1 space-y-2">
          <Label>Description</Label>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description..."
            className="min-h-[200px]"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={navigateBack}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!title || isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </FlexCol>
  );
};
